<template lang="pug">
    section.bookings-not-allowed(v-if="disabled")
        el-alert(type="info" :title="$t('Bookings are disabled for this event!')" :closable="false")
    section.bookings(v-else)
        //- App-Hero-Banner
        .app-hero(v-if="hasQrCodes")
            .app-hero-text
                p.app-hero-label {{ $t('Scanner-App') }}
                h3.app-hero-title {{ $t('Jetzt mit dem Smartphone scannen') }}
                p.app-hero-sub {{ $t('Öffnet direkt den QR-Code Scanner für dieses Event') }}
                .app-hero-login(v-if="appLoginOptions.length")
                    span.app-hero-login-label {{ $t('App-Benutzer Vorauswahl') }}
                    el-select(
                        v-model="appLoginEmail"
                        size="mini"
                        clearable
                        filterable
                        :placeholder="$t('Keine Vorauswahl')"
                    )
                        el-option(
                            v-for="option in appLoginOptions"
                            :key="option.email"
                            :label="option.label"
                            :value="option.email"
                        )
                a.app-hero-url(:href="appScanUrl" target="_blank" rel="noopener") {{ appScanUrl }}
            .app-hero-qr
                canvas(ref="appQrCanvas")
                el-button.app-hero-print(
                    type="text"
                    size="mini"
                    icon="el-icon-fa-print fas"
                    @click="openQrCodePdf"
                ) {{ $t('QR-Code drucken') }}

        paginated-items(
            :scope="scope"
            :type="key"
            :items="paginatedBookings"
            :show-filters="showFilters"
            :filters="pagination.filters"
            :variables="pagination.variables"
            :all-columns="allColumns"
            :active-columns="activeColumns"
            :page-info="pagination.pageInfo"
            :sort="sort"
            :sortables="sortableKeys"
            @click="openBookingModal"
            @change-filter="changeFilter"
            @change-sort="changeSort"
            @show-table-columns="changeTableColumns"
            @pagination-size-change="changePaginationSize"
            @pagination-current-change="changeCurrentPage"
        )
            div(slot="top")
                el-row.table-top(type="flex" justify="space-between")
                    .left
                        //- A booking always belongs to a slot, so without one there is nothing to book.
                        el-tooltip(
                            effect="dark"
                            placement="top"
                            :disabled="!isEventOptionDisabled(form)"
                            :content="newBookingDisabledReason"
                        )
                            span.new-booking-wrapper
                                el-button(
                                    :disabled="isEventOptionDisabled(form)"
                                    type="primary"
                                    size="mini"
                                    icon="el-icon-fa-plus fas"
                                    @click="openBookingModal"
                                )
                                    | {{ $t('New booking') }}
                    .right
                        el-button(
                            plain
                            size="mini"
                            type="primary"
                            icon="el-icon-fa-download fas"
                            :disabled="!bookings.length"
                            @click="pdfDialogVisible = true"
                        )
                            | {{ $t('Export') }}
                        el-button(
                            plain
                            size="mini"
                            type="primary"
                            icon="el-icon-fa-filter fas"
                            @click="toggleFilterVisiblity"
                            :disabled="disable.showFilters"
                        )
                            span(v-if="showFilters") {{ $t('Hide filters') }}
                            span(v-else) {{ $t('Show filters') }}
            template(v-slot:booker:prefix="{ item }")
                span.will-be-notified(v-if="item.notify")
                    i.el-icon-fa-paper-plane.fas
            template(v-slot:scanned="{ item }")
                template(v-if="item.scanned")
                    el-tag(
                        type="success"
                        size="mini"
                        disable-transitions
                    ) {{ $t('Scanned') }}
                    span.scanned-at(v-if="item.scanned_at") {{ scannedAtLabel(item) }}
                el-tag(
                    v-else
                    type="info"
                    size="mini"
                    disable-transitions
                ) {{ $t('Not scanned') }}
            template(v-slot:actions="{ item }")
                el-button.delete-button(
                    v-if="! item.deleted"
                    plain
                    circle
                    size="mini"
                    type="danger"
                    icon="el-icon-fa-trash-alt far"
                    @click.stop.prevent="deleteBooking(item)"
                )
                el-button.restore-button(
                    v-else
                    plain
                    circle
                    size="mini"
                    type="success"
                    icon="el-icon-fa-trash-restore fas"
                    @click.stop.prevent="restoreBooking(item)"
                )
        el-dialog(
            :title="modalTitle"
            :visible.sync="modalVisibility"
            :close-on-click-modal="false"
            top="10vh"
            width="860px"
            @opened="drawModalQr"
            @closed="resetModal"
        )
            el-form.booking-modal-form(
                :model="modal"
                label-position="top"
                :rules="rules"
                ref="modal"
                v-loading="modalLoading"
                @submit.prevent.native="submitModalForm"
            )
                .booking-modal-body
                    .booking-modal-fields
                        el-row
                            el-form-item(:label="$t('Event')" prop="event")
                                el-select.is-full-width(placeholder="" v-model="modal.event" valueKey="id" disabled)
                                    el-option(
                                        v-for="event in options.event.items"
                                        :disabled="isEventOptionDisabled(event)"
                                        :key="event.id"
                                        :label="getEventLabel(event)"
                                        :value="event"
                                    )

                        //- Mit Anmeldeformular: exakt dessen Raster und Reihenfolge, feste Felder
                        //- über die benannten Slots an den Booker / den Termin gebunden.
                        registration-field-inputs(
                            v-if="templateFields.length"
                            :fields="templateFields"
                            with-fixed
                            v-model="modal.answers"
                        )
                            template(#salutation="{ field }")
                                el-form-item(:label="field.label" prop="booker.gender")
                                    el-select.is-full-width(placeholder="" v-model="modal.booker.gender")
                                        el-option(:label="$t('Mr.')" value="M")
                                        el-option(:label="$t('Mrs.')" value="F")
                                        el-option(:label="$t('Undefined')" value="X")
                            template(#first_name="{ field }")
                                el-form-item(:label="field.label" prop="booker.first_name")
                                    el-input(v-model="modal.booker.first_name")
                            template(#last_name="{ field }")
                                el-form-item(:label="field.label" prop="booker.last_name")
                                    el-input(v-model="modal.booker.last_name")
                            template(#email="{ field }")
                                el-form-item(:label="field.label" prop="booker.email")
                                    el-input(v-model="modal.booker.email")
                            template(#phone="{ field }")
                                el-form-item(:label="field.label" prop="booker.phone")
                                    el-input(v-model="modal.booker.phone")
                            template(#slot="{ field }")
                                el-form-item(v-if="modal.event" :label="field.label" prop="slot")
                                    el-select.is-full-width(placeholder="" v-model="modal.slot" valueKey="id")
                                        el-option(
                                            v-for="slot in slotOptions"
                                            :disabled="isSlotOptionDisabled(slot)"
                                            :key="slot.id"
                                            :label="getSlotLabel(slot)"
                                            :value="slot"
                                        )

                        //- Ohne Anmeldeformular: die festen Felder in der Standardanordnung
                        template(v-else)
                            el-row.no-padding(:gutter="20")
                                el-col(:lg="6" :md="6")
                                    el-form-item(:label="$t('Gender')" prop="booker.gender")
                                        el-select.is-full-width(placeholder="" v-model="modal.booker.gender")
                                            el-option(:label="$t('Mr.')" value="M")
                                            el-option(:label="$t('Mrs.')" value="F")
                                            el-option(:label="$t('Undefined')" value="X")
                                el-col(:lg="9" :md="9")
                                    el-form-item(:label="$t('First name')" prop="booker.first_name")
                                        el-input(v-model="modal.booker.first_name")
                                el-col(:lg="9" :md="9")
                                    el-form-item(:label="$t('Last name')" prop="booker.last_name")
                                        el-input(v-model="modal.booker.last_name")
                            el-row.no-padding(:gutter="20")
                                el-col(:lg="12" :md="12")
                                    el-form-item(:label="$t('Email')" prop="booker.email")
                                        el-input(v-model="modal.booker.email")
                                el-col(:lg="12" :md="12")
                                    el-form-item(:label="$t('Phone')" prop="booker.phone")
                                        el-input(v-model="modal.booker.phone")
                            el-row
                                el-form-item(v-if="modal.event" :label="$t('Slot')" prop="slot")
                                    el-select.is-full-width(placeholder="" v-model="modal.slot" valueKey="id")
                                        el-option(
                                            v-for="slot in slotOptions"
                                            :disabled="isSlotOptionDisabled(slot)"
                                            :key="slot.id"
                                            :label="getSlotLabel(slot)"
                                            :value="slot"
                                        )

                    //- QR-Code der Anmeldung — wie im Anmeldungs-Formular
                    .qr-card(v-if="modalScanUrl")
                        .qr-card-canvas
                            canvas(ref="modalQrCanvas")
                        .qr-card-token {{ modal.qr_token }}
                        .qr-card-footer
                            el-tag(
                                v-if="modal.scanned"
                                type="success"
                                size="small"
                                effect="plain"
                            )
                                i.el-icon-fa-check.fas
                                |  {{ $t('Scanned') }}
                            el-tag(v-else type="info" size="small" effect="plain")
                                i.el-icon-fa-clock.fas
                                |  {{ $t('Not scanned') }}
                            span.qr-card-time(v-if="modal.scanned && modal.scanned_at") {{ scannedAtLabel(modal) }}
            template(slot="footer")
                .modal-footer
                    .left
                        .switch(v-if="modalType !== 'existing'")
                            el-switch(
                                :active-text="$t('Send email')"
                                v-model="modal.notify"
                            )
                    .right
                        el-button(
                            size="mini"
                            icon="el-icon-fa-times fas"
                            @click="closeModal"
                        ) {{ $t('Dismiss') }}
                        el-button(
                            :disabled="modalLoading"
                            size="mini"
                            type="primary"
                            icon="el-icon-fa-check fas"
                            @click="submitModalForm"
                        ) {{ $t('Ok') }}

        //- Export: Spaltenauswahl und PDF-Optionen (PDF / CSV)
        bookings-export-dialog(
            :visible="pdfDialogVisible"
            :event-id="form.id"
            :event-name="form.name"
            :bookings="bookings"
            :template-fields="templateFields"
            :qr-enabled="qrEnabled"
            :login-options="appLoginOptions"
            :login-email="appLoginEmail"
            @update:loginEmail="email => appLoginEmail = email"
            @export-pdf="openBookingsPdf"
            @close="pdfDialogVisible = false"
        )

        //- PDF-Vorschau (serverseitig gerendert)
        bookings-pdf-drawer(
            :visible="pdfDrawerVisible"
            :event-id="form.id"
            :columns="pdfSelectedColumns"
            :sort="pdfSort"
            :landscape="pdfLandscape"
            :show-qr-code="pdfShowQrCode"
            :login-email="appLoginEmail"
            :qr-only="pdfQrOnly"
            @close="pdfDrawerVisible = false"
        )
</template>

<i18n>
{
  "en": {
    "\"{field}\" is required.": "\"{field}\" is required."
  },
  "de": {
    "Scanner-App": "Scanner-App",
    "Jetzt mit dem Smartphone scannen": "Jetzt mit dem Smartphone scannen",
    "Öffnet direkt den QR-Code Scanner für dieses Event": "Öffnet direkt den QR-Code Scanner für dieses Event",
    "App-Benutzer Vorauswahl": "App-Benutzer Vorauswahl",
    "Keine Vorauswahl": "Keine Vorauswahl",
    "QR-Code drucken": "QR-Code drucken",
    "New booking": "Neue Anmeldung",
    "\"{field}\" is required.": "„{field}“ ist ein Pflichtfeld.",
    "Add a slot first. Every booking belongs to a slot.": "Lege zuerst einen Termin an. Jede Anmeldung gehört zu einem Termin.",
    "Bookings are not allowed for this event.": "Für dieses Event sind keine Anmeldungen zugelassen.",
    "Edit booking": "Booking bearbeiten",
    "Bookings are disabled for this event!": "Anmeldungen sind für dieses Event deaktiviert!",
    "Start": "Start",
    "End": "Ende",
    "Comment": "Kommentar",
    "Show filters": "Filter anzeigen",
    "Hide filters": "Filter verbergen",
    "Gender": "Geschlecht",
    "Salutation": "Anrede",
    "First name": "Vorname",
    "Last name": "Nachname",
    "Email": "E-Mail",
    "Phone": "Telefon",
    "Company": "Firma",
    "Mr.": "Herr",
    "Mrs.": "Frau",
    "Undefined": "Unbekannt",
    "Send email": "E-Mail versenden",
    "Scanned": "Gescannt",
    "Not scanned": "Nicht gescannt",
    "Booking scanned successfully": "Buchung erfolgreich gescannt",
    "Scan failed": "Scan fehlgeschlagen",
    "Export": "Exportieren",
    "Slot": "Slot",
    "Bookings": "Anmeldungen",
    "Dismiss": "Abbrechen"
  }
}
</i18n>
<script>

import _ from 'lodash'
import Vue from 'vue'
import uniqid from 'uniqid'
import QRCode from 'qrcode'

import pinia from '@/pinia'
import quillConfig from '@/quill/config'
import { timestamp2date } from  '@/filters'
import { useAuthStore, useSetupStore, useEventManagerEventStore, useEventManagerBookingStore } from '@/stores'

import { fetchAppLoginOptions } from '@/extensions/eventManager/utils/appLoginOptions'

import PaginatedItems from '@/components/PaginatedItems'
import RegistrationFieldInputs, { valuesToAnswers, answersToValues, findMissingRequiredAnswer, answersByField } from '@/extensions/eventManager/views/Partials/RegistrationFieldInputs'
import RegistrationTemplateApi from '@/extensions/eventManager/store/api/registrationTemplate'
import BookingsPdfDrawer from '@/extensions/eventManager/views/Event/Form/Partials/BookingsPdfDrawer'
import BookingsExportDialog from '@/extensions/eventManager/views/Partials/BookingsExportDialog'

export default {
    name: 'Bookings',
    data() {
        quillConfig.modules.link.pages = this.pageOptions
        return {
            scope: 'ex',
            key: 'evnt_event_booking',
            disable: {
                showFilters: false,
            },
            // Sortierung wie bei Members: klickbarer Spaltenkopf mit Pfeil hoch/runter
            sort: { field: 'booker', direction: 'asc' },
            sortableKeys: ['booker', 'email', 'start_at', 'end_at', 'created_at', 'scanned'],
            // Export-Dialog und die daraus uebernommenen PDF-Einstellungen
            pdfDialogVisible: false,
            pdfDrawerVisible: false,
            pdfSelectedColumns: ['name', 'email', 'phone', 'slot', 'registered', 'scanned'],
            pdfSort: 'name',
            pdfLandscape: false,
            pdfShowQrCode: true,
            pdfQrOnly: false,
            // Team-Zugaenge des Webauftritts fuer die Vorbelegung der App-Anmeldung
            appLoginOptions: [],
            appLoginEmail: '',
            modalVisibility: false,
            modalLoading: false,
            modalType: 'new', // valid options: 'new' / fresh / 'existing'
            // die Felder des Anmeldeformulars, das dem Event zugewiesen ist
            templateFields: [],
            modal: {
                id: undefined,
                event: undefined,
                booker: {
                    id: undefined,
                    gender: 'M',
                    first_name: '',
                    last_name: '',
                    email: '',
                    phone: '',
                    company: ''
                },
                slot: undefined,
                notify: true,
                answers: {}
            },
            pagination: {
                filters: {},
                variables: {
                    paginationSizes: [5, 10, 50],
                },
                pageInfo: {
                    currentPage: 1,
                    lastPage: 1,
                    perPage: 10,
                    total: 0,
                }
            },
            quillConfig,
        }
    },
    components: {
        RegistrationFieldInputs,
        PaginatedItems,
        BookingsPdfDrawer,
        BookingsExportDialog,
    },
    watch: {
        bookings(bookings) {
            this.pagination.pageInfo.total = bookings.length
            this.pagination.pageInfo.lastPage = Math.ceil(bookings.length / this.pagination.pageInfo.perPage)
        },
        hasQrCodes: {
            immediate: true,
            handler(val) {
                if (val) this.$nextTick(() => this.drawAppQr())
            }
        },
        // Vorbelegung steckt als ?login=… im QR-Code — neu zeichnen
        appLoginEmail() {
            if (this.hasQrCodes) this.$nextTick(() => this.drawAppQr())
        },
        // Der Dialog bleibt beim Wechsel zwischen Anmeldungen offen
        modalScanUrl(url) {
            if (url) this.$nextTick(() => this.drawModalQr())
        },
        // Zusatzfragen des zugewiesenen Anmeldeformulars nachladen
        registrationTemplateId: {
            immediate: true,
            handler() {
                this.loadTemplateFields()
            }
        },
    },
    computed: {
        bookings() {
            let bookings = []

            let slots = _.get(this.form, 'slots', [])
            slots.forEach(slot => {
                let slotBookings = _.cloneDeep(slot.bookings || [])
                _.map(slotBookings, booking => {
                    booking.slot = _.omit(slot, ['bookings'])
                    // Antworten je Feld-ID als Text, damit die Tabelle sie per value_path lesen kann
                    booking.answers = answersByField(booking.values)
                })
                bookings = [...bookings, ...slotBookings]
            })

            // //  apply filters
            // bookings = bookings.filter(booking => {
            //     let match = true
            //
            //     if (this.pagination.filters.id) {
            //         const filter = this.pagination.filters.id
            //         if (match && filter.from) {
            //             match = booking.id >= filter.from
            //         }
            //         if (match && filter.to) {
            //             match = booking.id <= filter.to
            //         }
            //     }
            //
            //     if (match && this.pagination.filters.start_at) {
            //         const filter = this.pagination.filters.start_at
            //         return booking.start_at >= filter.from
            //             && booking.start_at <= filter.to
            //     }
            //
            //     if (match && this.pagination.filters.end_at) {
            //         const filter = this.pagination.filters.end_at
            //         return booking.end_at >= filter.from
            //             && booking.end_at <= filter.to
            //     }
            //
            //     if (match && this.pagination.filters.comment) {
            //         const filter = this.pagination.filters.comment
            //         const search = _.toLower(filter.like)
            //         match = _.toLower(booking.comment).indexOf(search) >= 0
            //     }
            //
            //     return match
            // })

            // Sortierung nach aktiver Spalte + Richtung (klickbarer Spaltenkopf)
            const dir = this.sort.direction === 'desc' ? -1 : 1
            const path = _.get(_.find(this.allColumns, c => c.key === this.sort.field), 'value_path', 'booker.name')
            bookings = bookings.sort((a, b) => {
                const va = this.sort.field === 'booker' ? this.bookerSortKey(a) : _.get(a, path)
                const vb = this.sort.field === 'booker' ? this.bookerSortKey(b) : _.get(b, path)
                if (_.isNumber(va) || _.isNumber(vb)) return ((+va || 0) - (+vb || 0)) * dir
                if (_.isBoolean(va) || _.isBoolean(vb)) return ((va ? 1 : 0) - (vb ? 1 : 0)) * dir
                return String(va == null ? '' : va)
                    .localeCompare(String(vb == null ? '' : vb), 'de', { sensitivity: 'base', numeric: true }) * dir
            })

            return bookings
        },
        paginatedBookings() {
            let bookings = this.bookings

            const index = this.pagination.pageInfo.currentPage - 1
            const start = this.pagination.pageInfo.perPage * index
            const end = start + this.pagination.pageInfo.perPage

            bookings = bookings.slice(start, end)
            return bookings
        },
        activeColumns() {
            let activeColumns = [];

            const activeColumnKeys = _.get(this.user.settings, `${this.scope}.pagination.${this.key}.activeColumnKeys`, [])
            _.each(this.allColumns, column => {
                const allowed = _.find(activeColumnKeys, obj => column.key === obj.key);
                if (allowed) activeColumns.push(column);
            });

            if (! activeColumns.length) {
                activeColumns = this.allColumns.filter(column => column.default === true)
            }

            return activeColumns
        },
        showFilters() {
            return _.get(this.user.settings, `${this.scope}.pagination.${this.key}.showFilters`) || false
        },
        allColumns() {
            const filterOptionsFromTo = [
                {
                    key: 'from',
                    placeholder: 'von',
                    position: 10,
                },
                {
                    key: 'to',
                    placeholder: 'bis',
                    position: 20,
                }
            ]

            const columns = [
                {
                    key: 'id',
                    name: 'ID',
                    value_path: 'id',
                    filter: {
                        type: 'NumberFromTo',
                        options: filterOptionsFromTo
                    },
                    default: false,
                },
                {
                    key: 'booker',
                    name: 'Person',
                    value_path: 'booker.name',
                    filter: {
                        type: 'SingleInput',
                    },
                    default: true,
                },
                {
                    key: 'email',
                    name: 'E-Mail',
                    value_path: 'booker.email',
                    filter: {
                        type: 'SingleInput',
                    },
                    default: true,
                },
                {
                    key: 'start_at',
                    name: 'Start',
                    value_path: 'slot.start_at',
                    filter: {
                        type: 'DateTimeFromTo',
                        options: filterOptionsFromTo
                    },
                    default: true,
                },
                {
                    key: 'end_at',
                    name: 'Ende',
                    value_path: 'slot.end_at',
                    filter: {
                        type: 'DateTimeFromTo',
                        options: filterOptionsFromTo
                    },
                    default: true,
                },
                {
                    key: 'created_at',
                    name: 'Angemeldet am',
                    value_path: 'created_at',
                    filter: {
                        type: 'DateFromTo',
                        options: filterOptionsFromTo
                    },
                    default: true,
                },
                {
                    key: 'updated_at',
                    name: 'zuletzt bearbeitet',
                    value_path: 'updated_at',
                    filter: {
                        type: 'DateFromTo',
                        options: filterOptionsFromTo
                    },
                    default: false,
                },
            ]

            // Zusatzfragen des Anmeldeformulars, eine Spalte je Feld (unter "Spalten festlegen")
            this.templateFields.filter(field => !field.key).forEach(field => {
                columns.push({
                    key: `field:${field.id}`,
                    name: field.label,
                    value_path: `answers.${field.id}`,
                    sortable: false,
                    default: false,
                })
            })

            // Ohne QR-Codes wird nicht eingecheckt, dann gibt es auch nichts zu zeigen.
            // Eingecheckt bleibt immer die letzte Spalte.
            if (this.qrEnabled) {
                columns.push({
                    key: 'scanned',
                    name: this.$t('Scanned'),
                    value_path: 'scanned',
                    sortable: false,
                    default: true,
                })
            }

            return columns
        },
        registrationTemplateId() {
            return _.get(this.form, 'registrationTemplate.id') || null
        },
        newBookingDisabledReason() {
            if (_.get(this.form, 'booking') === 'none') return this.$t('Bookings are not allowed for this event.')
            return this.$t('Add a slot first. Every booking belongs to a slot.')
        },
        modalTitle() {
            let title = this.$t('New booking')
            if (this.modalType === 'existing') {
                title = this.$t('Edit booking')
            }
            return title
        },
        rules() {
            let rules = {
                event: 'required',
                booker: {
                    first_name: 'required',
                    last_name: 'required',
                    email: 'required|email'
                },
                slot: 'required'
            }

            // optional fixed fields the template switched to required
            const requiredFixed = { salutation: 'gender', phone: 'phone' }
            this.templateFields.forEach(field => {
                if (field.key && field.required && requiredFixed[field.key]) {
                    rules.booker[requiredFixed[field.key]] = 'required'
                }
            })

            // add booker.email exclude rule

            if (this.modal.slot) {
                const bookings = this.bookings.filter(booking => booking.slot.id === this.modal.slot.id)
                let excludedEmails = []
                bookings.forEach(booking => {
                    if (this.modalType !== 'existing' || this.modal.id !== booking.id)
                        excludedEmails.push(booking.booker.email)
                })

                if (excludedEmails.length) rules.booker.email += `|excluded:${excludedEmails.join(',')}`
            }

            return rules
        },
        slotOptions() {
            let options = []

            if (this.modal.event) {
                const events = _.get(this.options, 'event.items') || []
                let event = events.find(event => event.id === this.modal.event.id)
                if (event.id === this.form.id) event = this.form

                if (_.get(event, 'slots.length')) options = event.slots
            }

            return options
        },
        // "QR-Code verwenden" der Veranstaltung: steuert Scanner-Banner,
        // Eingecheckt-Spalte, QR-Karte im Dialog und die Export-Optionen
        qrEnabled() {
            return !!_.get(this.form, 'use_qr_code')
        },
        hasQrCodes() {
            return this.qrEnabled && this.bookings.some(b => !!b.qr_token)
        },
        // Scan-Link der bearbeiteten Anmeldung (nur bestehende haben einen Token)
        modalScanUrl() {
            if (!this.qrEnabled || this.modalType !== 'existing') return null

            const eventId = _.get(this.modal, 'slot.event.id') || _.get(this.form, 'id')
            const token = _.get(this.modal, 'qr_token')
            if (!eventId || !token) return null

            return `${window.location.origin}/event-manager/event/${eventId}/bookings?scan=${token}`
        },
        appScanUrl() {
            const url = `${window.location.origin}/app/event/${this.form.id}`
            if (!this.appLoginEmail) return url
            return `${url}?login=${encodeURIComponent(this.appLoginEmail)}`
        },
        disabled() {
            let disabled = true

            let bookings = []
            let slots = _.get(this.form, 'slots', [])
            slots.forEach(slot => {
                let slotBookings = slot.bookings || []
                bookings = [...bookings, ...slotBookings]
            })

            const booking = _.get(this.form, 'booking') || 'none'
            if (booking !== 'none' || bookings.length) {
                disabled = false
            }

            return disabled
        },
        setupStore() {
            return useSetupStore(pinia);
        },
        eventStore() {
            return useEventManagerEventStore();
        },
        bookingStore() {
            return useEventManagerBookingStore();
        },
        user() {
            return useAuthStore().user;
        },
        form() {
            return this.eventStore.form;
        },
        options() {
            return this.eventStore.options;
        },
    },
    methods: {
        scannedAtLabel(item) {
            const ts = _.get(item, 'scanned_at')
            return ts ? timestamp2date(ts, 'dd.MM.yyyy HH:mm') + ' Uhr' : ''
        },
        drawAppQr() {
            if (!this.$refs.appQrCanvas) return
            QRCode.toCanvas(this.$refs.appQrCanvas, this.appScanUrl, {
                width: 120,
                margin: 1,
                color: { dark: '#000000', light: '#ffffff' },
            })
        },
        drawModalQr() {
            if (!this.$refs.modalQrCanvas || !this.modalScanUrl) return
            QRCode.toCanvas(this.$refs.modalQrCanvas, this.modalScanUrl, { width: 180 })
        },
        // Team-Zugaenge des aktiven Webauftritts fuer die Vorauswahl laden
        loadAppLoginOptions() {
            return fetchAppLoginOptions(_.get(this.setupStore.activeCms, 'id'))
                .then(options => this.appLoginOptions = options)
        },
        toggleFilterVisiblity() {
            const visible = this.showFilters

            let value = false
            if (! visible) value = true

            this.disable.showFilters = true
            this.saveFilterVisibility({ scope: this.scope, key: this.key, value })
                .finally(() => this.disable.showFilters = false)
        },
        changeFilter(key, value) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.pagination.filters, key)
            } else {
                Vue.set(this.pagination.filters, key, value)
            }

            this.pagination.pageInfo.currentPage = 1
            this.pagination.pageInfo.total = this.bookings.length
            this.pagination.pageInfo.lastPage = Math.ceil(this.bookings.length / this.pagination.pageInfo.perPage)
        },
        changeTableColumns(visibleColumns) {

            let activeColumnKeys = []
            this.allColumns.forEach(column => {
                const visible = _.find(visibleColumns, value => column.key === value)
                if (visible) {
                    activeColumnKeys.push({ key: column.key })
                    column.default = true
                } else {
                    column.default = false
                }
            })

            // The setup store's action, not the auth store's setter: updateUserSettings only assigns
            // state.user.settings and never calls the API, so the choice looked applied and was gone
            // on the next load. saveActiveColumnKeys does both, and is what every other list uses.
            this.setupStore.saveActiveColumnKeys({
                scope: this.scope,
                key: this.key,
                data: activeColumnKeys,
            })
        },
        changePaginationSize(value) {
            this.pagination.pageInfo.currentPage = 1
            this.pagination.pageInfo.perPage = value
        },
        changeCurrentPage(value) {
            this.pagination.pageInfo.currentPage = value
        },
        openBookingModal(item = null) {
            this.modalLoading = true
            this.modalVisibility = true

            if (item && item instanceof Event === false) {
                this.modal = _.cloneDeep(item)
                this.$set(this.modal, 'answers', valuesToAnswers(this.templateFields, item.values))

                // find event option
                let id = _.get(this.modal, 'slot.event.id')
                if (id) {
                    this.modal.event = this.options.event.items.find(event => event.id === id)
                } else if (!this.isEventOptionDisabled(this.form)) {
                    this.modal.event = this.form
                }

                if (item && _.startsWith(item.id, 'new:')) {
                    this.modalType = 'fresh'
                } else {
                    this.modalType = 'existing'
                }
            } else {
                this.modal.id = uniqid('new:')
                this.modalType = 'new'
                this.$set(this.modal, 'answers', {})

                if (!this.isEventOptionDisabled(this.form)) {
                    this.modal.event = this.form
                }

                // const startAt = startOfHour(addDays(new Date(), 1))
                // this.modal.start_at = getUnixTime(startAt)
                // this.modal.end_at = getUnixTime(addHours(startAt, 1))
            }

            // is necessary for update
            this.modal.originalSlot = this.modal.slot

            this.modalLoading = false
        },
        closeModal() {
            this.modalVisibility = false
            this.modalLoading = false
        },
        submitModalForm() {
            this.$refs.modal.validate(valid => {
                if (valid) {
                    const missing = findMissingRequiredAnswer(this.templateFields, this.modal.answers)
                    if (missing) {
                        this.$notify.warning({
                            title: this.$t('Form is not valid!'),
                            message: this.$t('"{field}" is required.', { field: missing }),
                            iconClass: 'fas el-icon-fa-exclamation-circle',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000
                        })
                        return
                    }

                    // Antworten in das Eingabeformat der Mutation bringen; die Änderung markieren,
                    // damit die Event-API sie auch für bestehende Buchungen mitsendet.
                    this.modal.values = answersToValues(this.templateFields, this.modal.answers)
                    this.modal.valuesChanged = true

                    if (this.modalType === 'new') {
                        // add user to team
                        this.addBooking(this.modal)
                        this.closeModal()
                    } else {
                        // replace administrators in team-user
                        this.updateBooking(this.modal)
                        this.closeModal()
                    }
                }
            })
        },
        resetModal() {
            this.modal = {
                id: undefined,
                event: undefined,
                booker: {
                    id: undefined,
                    gender: 'M',
                    first_name: '',
                    last_name: '',
                    email: '',
                    phone: '',
                    company: ''
                },
                slot: undefined,
                notify: false,
                answers: {}
            }
            this.$refs.modal.resetFields()
        },
        getEventLabel(event) {
            let label = ''
            if (_.get(event, 'name')) label = event.name
            if (_.get(event, 'category.name')) label += ` - ${event.category.name}`
            return label
        },
        isEventOptionDisabled(event) {
            let disabled = false

            if (event.booking === 'none') disabled = true
            else if (!event.slots.length) disabled = true
            else if (
                event.member_limit_per_slot !== null
                && !event.slots.filter(slot => _.get(slot, 'bookings.length', 0) >= event.member_limit_per_slot)
            ) disabled = true

            return disabled
        },
        getSlotLabel(slot) {
            let label = ''

            if (slot.start_date === slot.end_date) {
                label = `${timestamp2date(slot.start_at)}, ${timestamp2date(slot.start_at, 'HH:mm')} - ${timestamp2date(slot.end_at, 'HH:mm')} Uhr`
            } else {
                label = `${timestamp2date(slot.start_at)}, ${timestamp2date(slot.start_at, 'HH:mm')} - ${timestamp2date(slot.end_at)}, ${timestamp2date(slot.end_at, 'HH:mm')}`
            }

            return label
        },
        isSlotOptionDisabled(slot) {
            let disabled = false

            // check in event if the current slot has capacity left

            const bookings = slot.bookings || []
            if (this.modal.event.booking === 'none') disabled = true
            else if (
                this.modal.event.member_limit_per_slot !== null
                && bookings.filter(booking => booking.id !== this.modal.id).length >= this.modal.event.member_limit_per_slot
            ) disabled = true

            return disabled
        },

        changeSort(key, direction) {
            this.sort = { field: key, direction }
            this.pagination.pageInfo.currentPage = 1
        },
        // Anzeigename einer Buchung (Fallbacks: name -> Vor/Nachname -> E-Mail)
        bookerDisplayName(booking) {
            const b = booking.booker || {}
            return b.name
                || [b.first_name, b.last_name].filter(n => n && n !== '-').join(' ')
                || b.email
                || ''
        },
        // Sortier-Schluessel: Anzeigename in Kleinschreibung
        bookerSortKey(booking) {
            return this.bookerDisplayName(booking).toLowerCase()
        },
        // Anmeldeliste als PDF: wird von der API gerendert (Dokumentvorlage
        // "Anmeldungen"), hier nur Vorschau mit Download/Druck oeffnen
        openBookingsPdf(settings) {
            this.pdfSelectedColumns = settings.columns
            this.pdfSort = settings.sort
            this.pdfLandscape = settings.landscape
            this.pdfShowQrCode = settings.showQrCode
            this.pdfQrOnly = false
            this.pdfDrawerVisible = true
        },
        // Aushang mit ausschliesslich dem QR-Code der Scanner-App
        // Das Anmeldeformular des Events liefert die Zusatzfragen für das Buchungsmodal.
        loadTemplateFields() {
            const id = this.registrationTemplateId
            if (!id) {
                this.templateFields = []
                return
            }
            RegistrationTemplateApi.fetchRegistrationTemplate(id)
                .then(template => {
                    if (this.registrationTemplateId !== id) return
                    this.templateFields = _.get(template, 'fields') || []
                })
                .catch(err => {
                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(err)
                })
        },
        openQrCodePdf() {
            this.pdfQrOnly = true
            this.pdfDrawerVisible = true
        },
        deleteBooking(booking) {
            this.eventStore.markFormBookingAsDeleted(booking);
        },
        restoreBooking(booking) {
            this.eventStore.unmarkFormBookingAsDeleted(booking);
        },
        addBooking(booking) {
            this.eventStore.addFormBooking(booking);
        },
        updateBooking(booking) {
            this.eventStore.updateFormBooking(booking);
        },
        saveFilterVisibility(payload) {
            return this.setupStore.saveFilterVisibility(payload);
        },
    },
    mounted() {
        this.pagination.pageInfo.total = this.bookings.length
        this.pagination.pageInfo.lastPage = Math.ceil(this.bookings.length / this.pagination.pageInfo.perPage)

        this.loadAppLoginOptions()

        const scanToken = this.$route.query.scan
        if (scanToken) {
            this.bookingStore.scanBooking(scanToken)
                .then(() => this.$message({ type: 'success', message: this.$t('Booking scanned successfully') }))
                .catch(() => this.$message({ type: 'error', message: this.$t('Scan failed') }))
        }
    }
}

</script>

<style lang="sass" scoped>
    // A disabled button swallows mouse events, so the tooltip hangs on this wrapper
    .new-booking-wrapper
        display: inline-block

    .scanned-at
        margin-left: 8px
        color: #909399
        font-size: 12px

    // Scanner-Banner: heller Blauverlauf aus der Element-Palette, dazu ein
    // dezentes Punktraster als Anspielung auf den QR-Code. Die weisse
    // QR-Kachel liegt mit leichtem Schatten darauf.
    .app-hero
        position: relative
        overflow: hidden
        display: flex
        align-items: center
        justify-content: space-between
        gap: 20px
        background: #fcfdff
        border: 1px solid $--border-color-lighter
        border-radius: 4px
        padding: 20px 24px
        margin-bottom: 0

        &::after
            content: ''
            position: absolute
            top: 0
            right: 0
            bottom: 0
            width: 55%
            background-image: radial-gradient($--color-primary-light-6 1.5px, transparent 1.5px)
            background-size: 12px 12px
            opacity: .35
            pointer-events: none
            // weiches Einsetzen statt harter Kante
            mask-image: linear-gradient(to right, transparent 0%, #000 65%)
            -webkit-mask-image: linear-gradient(to right, transparent 0%, #000 65%)

        .app-hero-text
            position: relative
            flex: 1
            min-width: 0

            .app-hero-label
                margin: 0 0 6px
                font-size: 11px
                font-weight: 600
                text-transform: uppercase
                letter-spacing: 1.5px
                color: $--color-primary

            .app-hero-title
                font-size: 18px
                font-weight: 700
                color: $--color-text-primary
                margin: 0 0 6px

            .app-hero-sub
                font-size: 13px
                color: $--color-text-regular
                margin: 0 0 10px

            .app-hero-login
                display: flex
                align-items: center
                gap: 10px
                margin-bottom: 10px

                .app-hero-login-label
                    font-size: 12px
                    color: $--color-text-secondary

                .el-select
                    width: 260px
                    ::v-deep .el-input__inner
                        color: $--color-text-primary
                        &::placeholder
                            color: $--color-text-regular

            .app-hero-url
                display: inline-block
                // padding: 2px 6px
                background: rgba(255, 255, 255, .75)
                border-radius: 3px
                font-family: monospace
                font-size: 11px
                color: $--color-text-secondary
                word-break: break-all
                text-decoration: none
                &:hover,
                &:focus
                    color: $--color-primary
                    text-decoration: underline

        .app-hero-qr
            position: relative
            z-index: 10
            flex-shrink: 0
            text-align: center
            background: $--color-white
            border: 1px solid $--color-primary-light-8
            border-radius: 4px
            padding: 10px
            box-shadow: 0 2px 10px rgba(0, 0, 0, .06)

            canvas
                display: block

            .app-hero-print
                margin-top: 6px
                padding: 0
                font-size: 12px

    .event
        .el-card__body
            & > section.bookings
                padding: 0
                ::v-deep
                    .column.booker
                        position: relative
                        .will-be-notified
                            position: absolute
                            left: -15px
                            top:  0
                            font-size: 11px
                            color: $--color-info

    // Bearbeiten-Dialog: Felder links, QR-Code der Anmeldung rechts daneben
    .booking-modal-body
        display: flex
        align-items: flex-start
        gap: 20px

        .booking-modal-fields
            flex: 1
            min-width: 0

    .qr-card
        flex-shrink: 0
        width: 280px
        display: flex
        flex-direction: column
        align-items: center
        gap: 12px
        padding: 20px 16px 16px
        background: $--background-color-base
        border: 1px solid $--border-color-lighter
        border-radius: 10px

        .qr-card-canvas
            background: $--color-white
            border-radius: 8px
            padding: 10px
            box-shadow: 0 1px 6px rgba(0, 0, 0, .08)
            canvas
                display: block

        .qr-card-token
            font-family: monospace
            font-size: 20px
            font-weight: 700
            letter-spacing: 4px
            color: $--color-text-primary
            background: $--color-white
            border: 1px solid $--border-color-light
            border-radius: 6px
            padding: 6px 16px

        .qr-card-footer
            display: flex
            align-items: center
            gap: 10px
            ::v-deep .el-tag i
                margin-right: 4px

        .qr-card-time
            font-size: 12px
            color: $--color-text-secondary

    .el-form > .el-form-item:last-child
        margin-bottom: 0
    .modal-footer
        display: flex
        justify-content: space-between
        .left
            display: flex
            align-items: center
            .switch
                height: 28px
                line-height: 28px

    // .el-date-editor.el-range-editor
    //     width: 100%
    //     ::v-deep
    //         input.el-range-input
    //             text-align: left
    //             padding-left: 4px

    .from-to-grid
        display: grid
        grid-template-columns: auto auto
        gap: 1rem

</style>
